import React from 'react';
import { Link } from 'react-router-dom';
import { AlertTriangle, ArrowLeft, ShieldAlert, Mail, Phone } from 'lucide-react';
import { Button } from '../components/ui/button';

import usePageSeo from '../hooks/usePageSeo';
const FraudAlert = () => {
  usePageSeo({
    title: 'Fraud Alert | WPAA',
    description: 'Important fraud alert from White Paper Accounts Auditing. Beware of impersonators and learn how to verify genuine WPAA communications.',
    path: '/fraud-alert'
  });

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <section className="pt-32 pb-12 px-4 bg-gradient-to-br from-red-900 via-red-800 to-gray-900 text-white">
        <div className="container mx-auto">
          <div className="flex items-center space-x-2 mb-4">
            <ShieldAlert className="text-red-400" size={28} />
            <span className="text-red-400 font-semibold text-sm uppercase tracking-wider">Important Notice</span>
          </div>
          <h1 className="text-4xl md:text-5xl font-bold mb-4">Fraud Alert</h1>
          <p className="text-gray-300">Protect yourself from scams and fraudulent activities</p>
        </div>
      </section>

      {/* Alert Banner */}
      <section className="py-6 px-4 bg-red-50 border-b border-red-200">
        <div className="container mx-auto">
          <div className="flex items-center space-x-3">
            <AlertTriangle className="text-red-600 flex-shrink-0" size={24} />
            <p className="text-red-800 font-medium">
              Be vigilant! Fraudsters may impersonate White Paper Accounts Auditing. Always verify communications through our official channels.
            </p>
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="py-16 px-4">
        <div className="container mx-auto max-w-4xl">
          <div className="bg-white rounded-xl shadow-lg p-8 md:p-12">
            
            <div className="prose prose-lg max-w-none">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Important Notice</h2>
              <p className="text-gray-600 mb-6">
                White Paper Accounts Auditing is committed to protecting our clients and the public from fraud. We have become aware of fraudulent activities where individuals or entities impersonate our firm to deceive people. Please read this notice carefully to protect yourself.
              </p>

              <h2 className="text-2xl font-bold text-gray-900 mb-4">How to Identify Fraud</h2>
              <p className="text-gray-600 mb-4">Be cautious of the following warning signs:</p>
              <ul className="list-disc pl-6 text-gray-600 mb-6 space-y-2">
                <li><strong>Unsolicited Contact:</strong> We do not cold call or send unsolicited emails asking for personal or financial information.</li>
                <li><strong>Pressure Tactics:</strong> Fraudsters often create urgency to force quick decisions. We never pressure clients.</li>
                <li><strong>Unofficial Email Addresses:</strong> Our official email domain is @wpaauae.com. Be wary of similar-looking domains.</li>
                <li><strong>Requests for Upfront Payment:</strong> We do not ask for payments through cryptocurrency, gift cards, or wire transfers to personal accounts.</li>
                <li><strong>Unrealistic Promises:</strong> Be cautious of guarantees of tax refunds, government grants, or other benefits that seem too good to be true.</li>
                <li><strong>Fake Job Offers:</strong> We do not send job offers requesting personal documents or payments for training.</li>
              </ul>

              <h2 className="text-2xl font-bold text-gray-900 mb-4">Our Official Contact Channels</h2>
              <div className="bg-teal-50 border border-teal-200 rounded-lg p-6 mb-6">
                <p className="text-teal-800 font-semibold mb-4">Verify all communications through these official channels:</p>
                <div className="space-y-3">
                  <div className="flex items-center space-x-3">
                    <Mail className="text-teal-600" size={20} />
                    <span className="text-gray-700"><strong>Email:</strong> info@wpaauae.com</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <Phone className="text-teal-600" size={20} />
                    <span className="text-gray-700"><strong>Phone:</strong> +971 50 121 9341</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <Phone className="text-teal-600" size={20} />
                    <span className="text-gray-700"><strong>Office:</strong> +971 6 744 7940</span>
                  </div>
                  <p className="text-gray-700"><strong>Address:</strong> 109, Midaffa Bldg., Musab Bin Omair St., Al Mahatta, Sharjah, UAE</p>
                  <p className="text-gray-700"><strong>Website:</strong> www.wpaauae.com</p>
                </div>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4">What To Do If You Suspect Fraud</h2>
              <ol className="list-decimal pl-6 text-gray-600 mb-6 space-y-2">
                <li><strong>Do Not Respond:</strong> Do not reply to suspicious emails or calls or provide any information.</li>
                <li><strong>Verify:</strong> Contact us directly through our official channels listed above to verify any communication.</li>
                <li><strong>Report:</strong> Report the incident to local authorities and relevant cybercrime units.</li>
                <li><strong>Document:</strong> Keep records of the fraudulent communication (emails, phone numbers, names used).</li>
                <li><strong>Inform Us:</strong> Let us know about the attempted fraud so we can alert others and take action.</li>
              </ol>

              <h2 className="text-2xl font-bold text-gray-900 mb-4">Report Fraud to Authorities</h2>
              <p className="text-gray-600 mb-4">If you have been a victim of fraud, please report to:</p>
              <ul className="list-disc pl-6 text-gray-600 mb-6 space-y-2">
                <li><strong>UAE Cybercrime:</strong> Report through eCrime portal or call Dubai Police (901)</li>
                <li><strong>Ministry of Economy:</strong> Consumer protection complaints</li>
                <li><strong>Central Bank of UAE:</strong> For financial fraud cases</li>
              </ul>

              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 mb-6">
                <div className="flex items-start space-x-3">
                  <AlertTriangle className="text-yellow-600 flex-shrink-0 mt-1" size={24} />
                  <div>
                    <p className="text-yellow-800 font-semibold mb-2">Remember:</p>
                    <p className="text-yellow-700">
                      White Paper Accounts Auditing will never ask you for passwords, PINs, OTPs, or full bank account details via email or phone. Always verify before sharing any sensitive information.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-8 pt-8 border-t">
              <Link to="/">
                <Button variant="outline" className="flex items-center space-x-2">
                  <ArrowLeft size={18} />
                  <span>Back to Home</span>
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default FraudAlert;
